import { useQuery } from '@tanstack/react-query';

import { useHasPermission } from '../hooks/useHasPermission';
import { FeatureFlagService, FeatureFlags, RawFeatureFlags } from '../services';

/**
 * Converts string values "true"/"false" to proper booleans.
 * API returns feature flags as strings, but we want to use them as booleans.
 */
const normalizeFeatureFlags = (flags: RawFeatureFlags): FeatureFlags => {
  const normalized: FeatureFlags = {};

  Object.entries(flags).forEach(([key, value]) => {
    if (value === 'true') {
      normalized[key] = true;
    } else if (value === 'false') {
      normalized[key] = false;
    }
    // undefined or other values are skipped (not included in normalized flags)
  });

  return normalized;
};

export const useFeatureFlagsQuery = () => {
  const queryKey = ['feature_flags'];
  // Only callers holding the permission may read flags; without this the request
  // 401s for roles such as reviewer and surfaces a permission toast on a lookup
  // the user never asked for. Consumers treat absent flags as off, so skipping
  // the fetch matches what those roles already see.
  const hasPermission = useHasPermission('configurations:read:list');

  const queryFn = async () => {
    const flags = await FeatureFlagService.list();

    return normalizeFeatureFlags(flags);
  };

  return useQuery<FeatureFlags>({
    queryKey,
    queryFn,
    staleTime: 5 * 60 * 1000, // 5 minutes - feature flags don't change often
    enabled: hasPermission,
  });
};

/**
 * Returns typed feature flags directly for convenient destructuring.
 * @example const { ccr_enabled } = useFeatureFlags();
 */
export const useFeatureFlags = (): FeatureFlags => {
  const { data } = useFeatureFlagsQuery();

  return { ...data };
};
